import { useState } from "react";
import {
  X,
  Bell,
  Check,
  BookOpen,
  AlertCircle,
  Clock,
  Trash2,
  RotateCcw,
  Settings,
  Archive,
} from "lucide-react";
import BackGroundColor from "../CommanComponents/BackGroundColor";

function NotificationModal({
  visable,
  Close,
  notifications = [],
  unreadCount = 0,
  onMarkAsRead,
  onMarkAllAsRead,
  onDeleteNotification,
  onClearReadNotifications,
  onClearAllNotifications,
  onSmartCleanup,
  onRespond,
}) {
  const [showCleanupMenu, setShowCleanupMenu] = useState(false);
  const [isCleanupLoading, setIsCleanupLoading] = useState(false);

  if (!visable) return null;

  const getNotificationStyle = (type) => {
    const styles = {
      class_invitation: {
        icon: <Archive className="w-5 h-5" />,
        bgColor: "bg-indigo-100",
        iconColor: "text-indigo-600",
        borderColor: "border-indigo-200",
      },
      class_created: {
        icon: <BookOpen className="w-5 h-5" />,
        bgColor: "bg-blue-100",
        iconColor: "text-blue-600",
        borderColor: "border-blue-200",
      },
      assignment_created: {
        icon: <AlertCircle className="w-5 h-5" />,
        bgColor: "bg-orange-100",
        iconColor: "text-orange-600",
        borderColor: "border-orange-200",
      },
      assignment_graded: {
        icon: <Check className="w-5 h-5" />,
        bgColor: "bg-green-100",
        iconColor: "text-green-600",
        borderColor: "border-green-200",
      },
      general_announcement: {
        icon: <Bell className="w-5 h-5" />,
        bgColor: "bg-purple-500",
        iconColor: "text-white",
        borderColor: "border-purple-200",
      },
    };
    return styles[type] || styles.general_announcement;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) return "Az önce";
    if (diffInHours < 24) return `${Math.floor(diffInHours)} saat önce`;
    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read && onMarkAsRead) {
      await onMarkAsRead(notification._id);
    }
  };

  const handleCleanup = async (type) => {
    setIsCleanupLoading(true);
    setShowCleanupMenu(false);
    try {
      if (type === "read" && onClearReadNotifications)
        await onClearReadNotifications();
      if (type === "smart" && onSmartCleanup) await onSmartCleanup(30);
      if (type === "all" && onClearAllNotifications) {
        if (
          window.confirm("Tüm bildirimleri silmek istediğinizden emin misiniz?")
        ) {
          await onClearAllNotifications();
        }
      }
    } catch (error) {
      console.error("İşlem hatası:", error);
    } finally {
      setIsCleanupLoading(false);
    }
  };

  const readNotificationsCount = notifications.filter((n) => n.read).length;

  return (
    <div className="fixed inset-0 bg-opacity-50 flex items-center justify-center z-25 p-4">
      <BackGroundColor />
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col z-50 overflow-hidden">
        {/* Header */}
        <div className="bg-yellow-500 flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-500 rounded-full">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Bildirimler</h2>
              <hr className="border-2 border-purple-500" />
              {unreadCount > 0 && (
                <p className="text-sm text-white">{unreadCount} okunmamış</p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <div className="relative">
              <button
                onClick={() => setShowCleanupMenu(!showCleanupMenu)}
                className="p-2 text-gray-600 hover:text-white transition-colors"
                disabled={isCleanupLoading}
              >
                <Settings className="w-5 h-5 text-purple-500 hover:text-white" />
              </button>
              {showCleanupMenu && (
                <div className="absolute right-0 top-12 bg-white shadow-lg border border-gray-200 rounded-lg py-2 w-48 z-10">
                  {readNotificationsCount > 0 && (
                    <button
                      onClick={() => handleCleanup("read")}
                      className="w-full px-4 py-2 text-left flex items-center space-x-2 text-orange-600 hover:bg-gray-50"
                    >
                      <Archive className="w-4 h-4 text-purple-500" />
                      <span>Okunmuşları Temizle</span>
                    </button>
                  )}
                  <button
                    onClick={() => handleCleanup("smart")}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 text-blue-600"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span>Akıllı Temizlik</span>
                  </button>
                  {notifications.length > 0 && (
                    <button
                      onClick={() => handleCleanup("all")}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Tümünü Sil</span>
                    </button>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={Close}
              className="p-2 text-gray-600 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-purple-500 hover:text-red-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto relative">
          {isCleanupLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Bell className="w-16 h-16 text-purple-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600">
                Henüz bildirim yok
              </h3>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => {
                const style = getNotificationStyle(notification.type);
                return (
                  <div
                    key={notification._id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors relative group ${!notification.read ? "bg-blue-50/50" : ""}`}
                  >
                    <div className="flex space-x-3">
                      <div
                        className={`flex-shrink-0 w-10 h-10 ${style.bgColor} ${style.iconColor} rounded-full flex items-center justify-center border ${style.borderColor}`}
                      >
                        {style.icon}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <h4
                            className={`text-sm font-semibold ${!notification.read ? "text-gray-900" : "text-gray-700"}`}
                          >
                            {notification.title}
                          </h4>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteNotification(notification._id);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <p className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </p>

                        {/* DAVET BUTONLARI - DOĞRU YERDE */}
                        {notification.type === "class_invitation" &&
                          !notification.read && (
                            <div
                              className="flex gap-2 mt-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() =>
                                  onRespond(
                                    notification.data.classId,
                                    "accept",
                                    notification._id,
                                  )
                                }
                                className="flex-1 bg-green-500 hover:bg-green-600 text-white text-xs font-bold py-2 px-3 rounded-lg shadow-sm transition-colors flex items-center justify-center gap-1"
                              >
                                <Check className="w-3 h-3" /> Kabul Et
                              </button>
                              <button
                                onClick={() =>
                                  onRespond(
                                    notification.data.classId,
                                    "reject",
                                    notification._id,
                                  )
                                }
                                className="flex-1 bg-red-500 hover:bg-red-600 text-white text-xs font-bold py-2 px-3 rounded-lg shadow-sm transition-colors flex items-center justify-center gap-1"
                              >
                                <X className="w-3 h-3" /> Reddet
                              </button>
                            </div>
                          )}

                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[10px] text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />{" "}
                            {formatDate(notification.createdAt)}
                          </span>
                          {notification.data?.className && (
                            <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                              {notification.data.className}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-400 text-center">
            {notifications.length} bildirim • {unreadCount} okunmamış
          </div>
        )}
      </div>
      {showCleanupMenu && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setShowCleanupMenu(false)}
        />
      )}
    </div>
  );
}

export default NotificationModal;
